import React, { useState, useEffect, useMemo } from "react";
import Card from "components/card";
import { useTenant } from "@contexts/TenantContext";
import { userDataService } from "@services/userDataService";
import { emailService, EmailThread, EmailMessage } from "@services/emailService";
import { EnhancedUser } from "types/profile.types";
import Avatar from "components/avatar/Avatar";
import {
  MdExpandMore,
  MdExpandLess,
  MdMail,
  MdOutbox,
  MdInbox,
  MdRefresh,
} from "react-icons/md";

const THREADS_PER_PAGE = 20;

const UserEmails = () => {
  const { currentTenant, currentTenantId } = useTenant();
  const [emailThreads, setEmailThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<string>("all");
  const [filterDirection, setFilterDirection] = useState<string>("all");
  const [visibleCount, setVisibleCount] = useState(THREADS_PER_PAGE);

  useEffect(() => {
    if (currentTenantId) {
      fetchEmailsForTenant(currentTenantId);
    } else {
      setEmailThreads([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId]);

  const fetchEmailsForTenant = async (tenantId: string) => {
    setLoading(true);
    setError(null);

    try {
      const users = await userDataService.fetchUsersForTenant(tenantId);

      const userList = users.map((user: EnhancedUser) => ({
        uid: user.uid,
        displayName: user.displayName,
        email: user.profile?.email || user.authEmail || "",
      }));

      const threads = await emailService.fetchEmailsForUsers(userList);
      setEmailThreads(threads);
    } catch (err) {
      console.error("Error fetching emails:", err);
      setError("Failed to load emails. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (currentTenantId && !loading) {
      setVisibleCount(THREADS_PER_PAGE);
      fetchEmailsForTenant(currentTenantId);
    }
  };

  // Toggle thread expansion
  const toggleThread = (threadKey: string) => {
    setExpandedThreads((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(threadKey)) {
        newSet.delete(threadKey);
      } else {
        newSet.add(threadKey);
      }
      return newSet;
    });
  };

  // Toggle message expansion
  const toggleMessage = (messageKey: string) => {
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageKey)) {
        newSet.delete(messageKey);
      } else {
        newSet.add(messageKey);
      }
      return newSet;
    });
  };

  // Get email type display info
  const getEmailTypeInfo = (type?: string) => {
    const types: Record<string, { label: string; color: string }> = {
      connection: { label: "Connection", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
      feedback: { label: "Feedback", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
      welcomePackage: { label: "Welcome", color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" },
      introConsent: { label: "Consent", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
      checkIn: { label: "Check-In", color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200" },
    };
    return types[type || ""] || { label: type || "Other", color: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200" };
  };

  // Format date for display
  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else if (days === 1) {
      return "Yesterday";
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  // Get thread-level direction summary
  const getThreadDirectionSummary = (thread: EmailThread) => {
    let inbound = 0;
    let outbound = 0;
    for (const msg of thread.messages) {
      if (msg.metadata?.direction === "inbound") inbound++;
      else if (msg.metadata?.direction === "outbound") outbound++;
    }
    return { inbound, outbound, hasReply: inbound > 0 };
  };

  // Render message content
  const renderMessageContent = (message: EmailMessage, isExpanded: boolean) => {
    const content = message.content.plain || message.content.extractedText || "";
    const truncatedContent = content.length > 200 ? content.slice(0, 200) + "..." : content;

    return (
      <div className="text-sm text-gray-600 dark:text-gray-300">
        {isExpanded ? (
          <div className="whitespace-pre-wrap">{content}</div>
        ) : (
          <div className="line-clamp-2">{truncatedContent}</div>
        )}
      </div>
    );
  };

  // Get unique email types for filter
  const emailTypes = useMemo(
    () => Array.from(new Set(emailThreads.map((t) => t.email_type).filter(Boolean))),
    [emailThreads]
  );

  // Filter threads based on search, type, and direction
  const filteredThreads = useMemo(() => {
    return emailThreads.filter((thread) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        thread.subject.toLowerCase().includes(searchLower) ||
        thread.userName.toLowerCase().includes(searchLower) ||
        thread.userEmail.toLowerCase().includes(searchLower) ||
        thread.messages.some(
          (m) =>
            m.content.plain?.toLowerCase().includes(searchLower) ||
            m.sender.email.toLowerCase().includes(searchLower)
        );

      const matchesType = filterType === "all" || thread.email_type === filterType;

      const matchesDirection =
        filterDirection === "all" ||
        thread.messages.some((m) => m.metadata?.direction === filterDirection);

      return matchesSearch && matchesType && matchesDirection;
    });
  }, [emailThreads, searchTerm, filterType, filterDirection]);

  // Paginated threads
  const paginatedThreads = filteredThreads.slice(0, visibleCount);
  const hasMore = visibleCount < filteredThreads.length;

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(THREADS_PER_PAGE);
  }, [searchTerm, filterType, filterDirection]);

  if (!currentTenant) {
    return (
      <Card extra={"w-full h-full mt-3 flex items-center justify-center"}>
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">
            Please select a tenant to view emails.
          </p>
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card extra={"w-full h-full mt-3 flex items-center justify-center"}>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading emails...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card extra={"w-full h-full mt-3 flex items-center justify-center"}>
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => currentTenantId && fetchEmailsForTenant(currentTenantId)}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="mt-3">
      <Card extra={"w-full h-full p-6"}>
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
              <MdMail className="h-6 w-6" />
              User Emails
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              View email threads for users in {currentTenant.title}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-navy-700 rounded-lg hover:bg-gray-200 dark:hover:bg-navy-600 transition-colors disabled:opacity-50"
          >
            <MdRefresh className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by subject, user, or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-navy-800 text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <select
            value={filterDirection}
            onChange={(e) => setFilterDirection(e.target.value)}
            className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-navy-800 text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="all">All Directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-navy-800 text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="all">All Types</option>
            {emailTypes.map((type) => (
              <option key={type} value={type}>
                {getEmailTypeInfo(type).label}
              </option>
            ))}
          </select>
        </div>

        {/* Email Threads */}
        <div className="space-y-4">
          {paginatedThreads.length === 0 ? (
            <div className="text-center py-12">
              <MdMail className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm || filterType !== "all" || filterDirection !== "all"
                  ? "No emails found matching your filters."
                  : "No emails found for users in this tenant."}
              </p>
            </div>
          ) : (
            paginatedThreads.map((thread) => {
              const threadKey = `${thread.userId}-${thread.threadId}`;
              const isThreadExpanded = expandedThreads.has(threadKey);
              const typeInfo = getEmailTypeInfo(thread.email_type);
              const dirSummary = getThreadDirectionSummary(thread);

              return (
                <div
                  key={threadKey}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  {/* Thread Header */}
                  <div
                    className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-navy-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors"
                    onClick={() => toggleThread(threadKey)}
                  >
                    <Avatar
                      uid={thread.userId}
                      displayName={thread.userName}
                      size="small"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-800 dark:text-white truncate">
                          {thread.userName}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({thread.userEmail})
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                        {thread.subject}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* Thread direction summary */}
                      <div className="flex items-center gap-1">
                        {dirSummary.outbound > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-blue-600 dark:text-blue-400" title="Outbound emails sent">
                            <MdOutbox className="h-3.5 w-3.5" />
                            {dirSummary.outbound}
                          </span>
                        )}
                        {dirSummary.inbound > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-green-600 dark:text-green-400" title="Inbound replies received">
                            <MdInbox className="h-3.5 w-3.5" />
                            {dirSummary.inbound}
                          </span>
                        )}
                      </div>
                      {dirSummary.hasReply && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                          Replied
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(thread.lastActivity)}
                      </span>
                      {isThreadExpanded ? (
                        <MdExpandLess className="h-5 w-5 text-gray-400" />
                      ) : (
                        <MdExpandMore className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Thread Messages */}
                  {isThreadExpanded && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {thread.messages.map((message) => {
                        const messageKey = `${thread.userId}-${message.messageId}`;
                        const isMessageExpanded = expandedMessages.has(messageKey);
                        const isOutbound = message.metadata?.direction === "outbound";

                        return (
                          <div
                            key={messageKey}
                            className="p-4 bg-white dark:bg-navy-900"
                          >
                            {/* Message Header */}
                            <div className="flex items-start gap-3 mb-2">
                              <div
                                className={`p-1.5 rounded-full ${
                                  isOutbound
                                    ? "bg-blue-100 dark:bg-blue-900"
                                    : "bg-green-100 dark:bg-green-900"
                                }`}
                              >
                                {isOutbound ? (
                                  <MdOutbox className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                ) : (
                                  <MdInbox className="h-4 w-4 text-green-600 dark:text-green-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-gray-800 dark:text-white">
                                    {message.sender.name || message.sender.email}
                                  </span>
                                  {message.sender.name && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      &lt;{message.sender.email}&gt;
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {message.timestamp.toLocaleString()}
                                </p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMessage(messageKey);
                                }}
                                className="text-xs text-brand-500 hover:text-brand-600 hover:underline"
                              >
                                {isMessageExpanded ? "Show less" : "Show more"}
                              </button>
                            </div>

                            {/* Subject (if different from thread) */}
                            {message.subject !== thread.subject && (
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Subject: {message.subject}
                              </p>
                            )}

                            {/* Message Content */}
                            {renderMessageContent(message, isMessageExpanded)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setVisibleCount((prev) => prev + THREADS_PER_PAGE)}
              className="px-6 py-2 text-sm font-medium text-brand-500 bg-brand-50 dark:bg-brand-900/20 rounded-lg hover:bg-brand-100 dark:hover:bg-brand-900/40 transition-colors"
            >
              Load more ({filteredThreads.length - visibleCount} remaining)
            </button>
          </div>
        )}

        {/* Footer Stats */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {paginatedThreads.length} of {filteredThreads.length} email threads
            {filteredThreads.length !== emailThreads.length && ` (${emailThreads.length} total)`}
          </p>
        </div>
      </Card>
    </div>
  );
};

export default UserEmails;
